import { useEffect } from 'react'

/**
 * The listener installed while unsaved changes exist, or null. Module scope,
 * because a caller that is about to navigate away needs to remove it in the
 * same tick: the effect cleanup below runs only after React commits the
 * change that disarms the prompt, and a navigation started before that
 * commit still meets the listener.
 */
let armedListener: ((event: BeforeUnloadEvent) => string) | null = null

const disarm = (): void => {
  if (armedListener === null) return
  window.removeEventListener('beforeunload', armedListener)
  armedListener = null
}

/** Remove the leave prompt now, before a navigation the caller starts next. */
export const disarmUnsavedChangesWarning = (): void => disarm()

export const useUnsavedChangesWarning = (when: boolean): void => {
  useEffect(() => {
    if (!when) return undefined
    const handleBeforeUnload = (event: BeforeUnloadEvent): string => {
      // preventDefault() is the modern signal; returnValue the legacy one — browsers need both.
      event.preventDefault()
      event.returnValue = ''
      return ''
    }
    disarm()
    window.addEventListener('beforeunload', handleBeforeUnload)
    armedListener = handleBeforeUnload
    return () => {
      if (armedListener === handleBeforeUnload) disarm()
    }
  }, [when])
}

export default useUnsavedChangesWarning
